import { CheckOutlined } from '@ant-design/icons'
import { Avatar, Input } from '@lmc/ui-kit'
import { Button, Text } from '@lmc/ui-kit/src/general'
import { Divider, Popover, Spin, Tag } from 'antd'
import { lowerFirst } from 'lodash'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import useAllocateCarer from 'screens/Rostering/hooks/useAllocateCarer'
import useAllocationList from 'screens/Rostering/hooks/useAllocationList'
import styled from 'styled-components'
import ExpandIcon from '../icons/ExpandIcon'

const QuickAllocationPopover = ({
  children,
  isOpen,
  setIsOpen,
  setAllocationWindowVisible,
  revisionId
}: any) => {
  const { t } = useTranslation()
  const [searchTerm, setSearchTerm] = useState('')
  const [addedUsersIds, setAddedUsersIds] = useState<string[]>([])
  const [removedUsersIds, setRemovedUsersIds] = useState<string[]>([])

  const { allocationList, numberOfSupply, selectedUsers, isListLoading } =
    useAllocationList({
      id: revisionId,
      allocationListFilters: {
        availability: ['Available and Selected']
      },
      skipPagination: true
    })

  const { allocateCarer } = useAllocateCarer({
    id: revisionId
  })

  const filteredAllocationList = useMemo(() => {
    if (!searchTerm.trim()) return allocationList

    const searchLower = searchTerm.toLowerCase()
    return allocationList?.filter((user: any) => {
      const fullName = `${user.name.first} ${user.name.last}`.toLowerCase()
      return (
        user.name.first.toLowerCase().includes(searchLower) ||
        user.name.last.toLowerCase().includes(searchLower) ||
        fullName.includes(searchLower)
      )
    })
  }, [allocationList, searchTerm])

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value)
  }

  const handleOpenChange = (newOpen: boolean) => {
    setIsOpen(newOpen)
  }

  const handleCancel = () => {
    setIsOpen(false)
  }

  const handleExpandIconClick = (e: React.MouseEvent) => {
    setAllocationWindowVisible(true)
    setIsOpen(false)
  }

  const handlePopoverClick = (e: React.MouseEvent) => {
    e.stopPropagation()
  }

  const handleUserItemClick = (
    userId: string,
    wasSelected: boolean,
    isClockedIn: boolean
  ) => {
    if (isClockedIn && wasSelected) {
      return
    }

    if (addedUsersIds.includes(userId)) {
      setAddedUsersIds((prev) => prev.filter((id) => id !== userId))
      return
    }

    if (removedUsersIds.includes(userId)) {
      setRemovedUsersIds((prev) => prev.filter((id) => id !== userId))
      return
    }

    if (wasSelected) {
      setRemovedUsersIds((prev) => [...prev, userId])
    } else {
      setAddedUsersIds((prev) => [...prev, userId])
    }
  }

  const handleSave = async () => {
    if (addedUsersIds.length > 0 || removedUsersIds.length > 0) {
      allocateCarer({
        add: addedUsersIds.map((id) => ({
          teamMemberId: id,
          role: 'carer'
        })),
        remove: removedUsersIds,
        quickAllocation: true
      })
    }
    handleCancel()
  }

  const isUserSelected = (userId: string, wasSelected: boolean) => {
    if (addedUsersIds.includes(userId)) return true
    if (removedUsersIds.includes(userId)) return false
    return wasSelected
  }

  const renderUserStatus = (user: any) => {
    if (user.clockedOut) {
      return (
        <StyledTag color="green">
          <Text level={2}>{t('CLOCKED_OUT')}</Text>
        </StyledTag>
      )
    }

    if (user.clockedIn) {
      return (
        <StyledTag color="purple">
          <Text level={2}>{t('CLOCKED_IN')}</Text>
        </StyledTag>
      )
    }

    if (isUserSelected(user.id, user.selected)) {
      return <StyledCheckIcon />
    }

    return null
  }

  return (
    <div onClick={handlePopoverClick}>
      <Popover
        placement="bottomLeft"
        trigger="click"
        open={isOpen}
        onOpenChange={handleOpenChange}
        content={
          <ContentContainer>
            <HeaderContainer>
              <Input
                placeholder={t('SEARCH')}
                value={searchTerm}
                onChange={handleSearchChange}
              />
              <ExpandIcon onClick={handleExpandIconClick} />
            </HeaderContainer>

            <UsersListContainer>
              {isListLoading ? (
                <LoadingContainer>
                  <Spin />
                </LoadingContainer>
              ) : (
                filteredAllocationList?.map((user: any) => (
                  <UserItem
                    key={user.id}
                    isSelected={isUserSelected(user.id, user.selected)}
                    isClockedInOrOut={
                      user.selected && (user.clockedIn || user.clockedOut)
                    }
                    onClick={() =>
                      handleUserItemClick(
                        user.id,
                        user.selected,
                        user.clockedIn
                      )
                    }
                  >
                    <UserInfo>
                      <Avatar
                        size="small"
                        url={
                          (user.avatar ||
                            user.picture ||
                            undefined) as unknown as string
                        }
                      />
                      <StyledUserText
                        isClockedInOrOut={
                          user.selected && (user.clockedIn || user.clockedOut)
                        }
                      >
                        {`${user.name.first} ${user.name.last}`}
                      </StyledUserText>
                    </UserInfo>
                    {renderUserStatus(user)}
                  </UserItem>
                ))
              )}
            </UsersListContainer>

            <FooterContainer>
              <StyledDivider />
              <FooterContentContainer>
                <StyledText>
                  {`${
                    selectedUsers +
                    addedUsersIds.length -
                    removedUsersIds.length
                  }/${numberOfSupply} ${lowerFirst(t('ALLOCATED'))}`}
                </StyledText>
                <ButtonContainer>
                  <Button variant="text" onClick={() => handleCancel()}>
                    {t('CANCEL')}
                  </Button>
                  <Button variant="primary" onClick={() => handleSave()}>
                    {t('SAVE')}
                  </Button>
                </ButtonContainer>
              </FooterContentContainer>
            </FooterContainer>
          </ContentContainer>
        }
      >
        {children}
      </Popover>
    </div>
  )
}

const ContentContainer = styled.div`
  min-width: 330px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`
const HeaderContainer = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`
const FooterContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`
const StyledText = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color3};
`
const ButtonContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`
const FooterContentContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`
const StyledDivider = styled(Divider)`
  margin: 0;
`

const UsersListContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  max-height: 30vh;
  overflow-y: auto;
`

const UserItem = styled.div<{
  isSelected?: boolean
  isClockedInOrOut?: boolean
}>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 16px;
  border-radius: 8px;
  cursor: ${({ isClockedInOrOut, isSelected }) =>
    isClockedInOrOut && isSelected ? 'not-allowed' : 'pointer'};
  background-color: ${({ isSelected, isClockedInOrOut, theme }) => {
    if (isClockedInOrOut && isSelected) {
      return theme.color.neutral.color8
    }
    if (isSelected) {
      return `${theme.color.status.primary}33`
    }
    return 'transparent'
  }};
`
const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 24px;
`
const StyledCheckIcon = styled(CheckOutlined)`
  color: ${({ theme }) => theme.color.status.primary};
`
const StyledTag = styled(Tag)`
  margin: 0;
  padding: 3px 8px;
`
const StyledUserText = styled(Text)<{ isClockedInOrOut?: boolean }>`
  color: ${({ isClockedInOrOut, theme }) =>
    isClockedInOrOut ? theme.color.neutral.color5 : theme.color.neutral.color3};
`

export default QuickAllocationPopover
